import * as i18next from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import i18nextSprintf from 'i18next-sprintf-postprocessor';
import { cloneDeep, get, isString } from 'lodash-es';
import { Settings } from 'luxon';

import type { Intl as IntlGenerated, IntlPlural as IntlPluralGenerated } from 'locales/generated/localeKeys';

import { isChromatic } from './chromaticUtils';
import { importWithStaleChunkRecovery } from './chunkRecoveryUtils';
import { getTimeZone } from './dateUtils';
import { isLocallyServed } from './runtimeUtils';
import { localeQueryOverrideStorage, localeStorage, timeZoneStorage } from './storage/intl';

export enum Locale {
  EN_CA = 'en-CA',
  EN_US = 'en-US',
  ES_US = 'es-US',
  FR_CA = 'fr-CA',
}

export enum MultilingualStringKey {
  EN = 'en',
  ES = 'es',
  FR = 'fr',
}

export type Intl = IntlGenerated;

/**
 * Base plural translation keys without _one/_other suffixes (generated from locale files)
 */
export type IntlPlural = IntlPluralGenerated;

export const getMultilingualValueFromLocale = (locale: Locale) =>
  ({
    [Locale.EN_CA]: MultilingualStringKey.EN,
    [Locale.EN_US]: MultilingualStringKey.EN,
    [Locale.ES_US]: MultilingualStringKey.ES,
    [Locale.FR_CA]: MultilingualStringKey.FR,
  })[locale];

export const getLocaleFromMultilingualValue = (value: MultilingualStringKey): Locale =>
  ({
    [MultilingualStringKey.EN]: Locale.EN_CA,
    [MultilingualStringKey.ES]: Locale.ES_US,
    [MultilingualStringKey.FR]: Locale.FR_CA,
  })[value];

const localeImportMap: Record<string, () => Promise<{ default: Record<string, string> }>> = {
  'en-CA': () => importWithStaleChunkRecovery(() => import('../locales/en.json')),
  'fr-CA': () => importWithStaleChunkRecovery(() => import('../locales/fr.json')),
  /*
   * TODO: (ED-18358) Re-enable Spanish support when needed.
   * 'es-US': () => importWithStaleChunkRecovery(() => import('../locales/es.json')),
   */
};

export const LANG_QUERY_PARAM = 'lang';

const getLocaleFromQueryParameter = (): Locale | undefined => {
  const parameters = new URLSearchParams(globalThis.location.search);
  const langParameter = parameters.get(LANG_QUERY_PARAM);

  if (!langParameter) {
    return undefined;
  }

  // BCP 47 tags are case-insensitive (RFC 5646) — normalize to match localeImportMap keys
  const matchedLocale = Object.keys(localeImportMap).find(key => key.toLowerCase() === langParameter.toLowerCase());

  if (!matchedLocale) {
    return undefined;
  }

  parameters.delete(LANG_QUERY_PARAM);
  const newSearch = parameters.toString();
  const newUrl = `${globalThis.location.pathname}${newSearch ? `?${newSearch}` : ''}${globalThis.location.hash}`;

  try {
    globalThis.history.replaceState(globalThis.history.state, '', newUrl);
  } catch {
    // ReplaceState can throw SecurityError in sandboxed iframes — locale still works, URL just keeps ?lang
  }

  return matchedLocale as Locale;
};

const getLocaleFromBrowser = (): Locale | undefined => {
  const languages = globalThis.navigator?.languages ?? (globalThis.navigator?.language ? [navigator.language] : []);

  // TODO: (ED-18293) Add `es: Locale.ES_US` when Spanish support is re-enabled in localeImportMap.
  const primaryTagToLocale: Record<string, Locale> = {
    en: Locale.EN_CA,
    fr: Locale.FR_CA,
  };

  for (const tag of languages) {
    const matched = primaryTagToLocale[tag.split('-', 1)[0].toLowerCase()];
    if (matched) {
      return matched;
    }
  }

  return undefined;
};

export const isMatchingLocale = (locale: Locale) => localeStorage.get() === locale;
export const isEnCA = () => isMatchingLocale(Locale.EN_CA);
export const isFrCA = () => isMatchingLocale(Locale.FR_CA);
export const isEsUs = () => isMatchingLocale(Locale.ES_US);

/**
 * This bootSequence is what gets called before the app is rendered. It loads locale resources on demand
 * via dynamic `import()`, so only the needed locale JSON is downloaded — not all locales upfront.
 * It also sets the timezone in storage and initializes Luxon.
 */
export const bootSequence = async (locale = Locale.EN_CA): Promise<Locale> => {
  const currentTimeZone = getTimeZone();

  timeZoneStorage.set(currentTimeZone);

  const queryParameterLocale = getLocaleFromQueryParameter();
  let currentLocale: Locale;

  if (queryParameterLocale) {
    currentLocale = queryParameterLocale;
    localeStorage.set(currentLocale);
    localeQueryOverrideStorage.set(true);
  } else {
    localeQueryOverrideStorage.remove();
    const storedLocale = localeStorage.get();
    currentLocale = storedLocale ?? getLocaleFromBrowser() ?? locale;
    if (!storedLocale) {
      localeStorage.set(currentLocale);
    }
  }

  Settings.defaultZone = currentTimeZone;

  await i18next
    .use(i18nextSprintf)
    .use(resourcesToBackend((language: string) => localeImportMap[language]?.() ?? localeImportMap['en-CA']()))
    .init({
      debug: isLocallyServed() && process.env.REACT_APP_FEATURE_I18NEXT_DEBUG === 'true',
      /**
       *  We want to force english locale on chromatic ui-test to omit any inconsistent
       *  snapshot behaviour due to locale
       *
       *  @link https://www.chromatic.com/docs/ischromatic
       */
      fallbackLng: Locale.EN_CA,
      interpolation: {
        skipOnVariables: false,
      },
      lng: isChromatic() ? Locale.EN_CA : currentLocale,
      load: 'currentOnly',
      preload: [Locale.EN_CA],
      returnEmptyString: false,
    });

  return currentLocale;
};

export type Translate = typeof translate;

export type TranslateFn = (namespace: Intl, args?: (number | string)[], options?: i18next.TOptions) => string;

export const translate = Object.freeze({
  /**
   * Get i18next translations with/without sprintf support.
   * Used with static singular/plural keys, for dynamic keys based on
   * a `count`, use `tPlural` instead.
   *
   * Example:
   *  import { translate } from 'utils/intlUtils';
   *  const { t } = translate;
   *
   *  // Without sprintf support
   *  t('key');
   *
   *  // With sprintf support
   *  t('key', ['5', 'days']); // Due in %d %s ---> 'Due in 5 days'
   *
   * @param {Intl} namespace - The key name (singular/plural) of the desired translation
   * @param {(string|number)[]} args - An array of sprintf tokens to process
   * @param {i18next.TOptions} options - Conforms to useTranslation's options config and not localized.
   */
  t: (namespace: Intl, args: (number | string)[] = [], options: i18next.TOptions = {}) => {
    if (args.length > 0) {
      return i18next.t(namespace, { postProcess: 'sprintf', sprintf: args, ...options });
    }

    const hasCapitalLetters = namespace !== namespace.toLocaleLowerCase();
    const hasWhitespace = namespace.includes(' ');
    const isInvalidLokaliseKeyFormat = hasCapitalLetters || hasWhitespace;
    if (isInvalidLokaliseKeyFormat) {
      return namespace;
    }

    return i18next.t(namespace, options);
  },

  /**
   * Gets the singular or plural variation of a translation based on the `count` provided.
   * Different languages have different rules for plurals, thus the dynamic key to be used
   * is automatically handled by `i18next`.
   *
   * English language example:
   *  tPlural('car', 0); // 'Cars'
   *  tPlural('car', 1); // 'Car'
   *  tPlural('car', 2); // 'Cars'
   *  tPlural('car', amountOfCars, [amountOfCars]); // '5 Cars', '1 Car'
   *
   * You can read more on i18next plurals here:
   * https://www.i18next.com/translation-function/plurals
   * https://edealer-wiki.netlify.app/documentation/localization.html#clients
   *
   * @param {Intl} namespace - The singular key name of the desired translation
   * @param {object} count - The value to determine whether to use the singular/plural key
   */
  tPlural: (namespace: IntlPlural, count: number, args: (number | string)[] = [], options: i18next.TOptions = {}) =>
    i18next.t(namespace, { count: count || 0, postProcess: 'sprintf', sprintf: args, ...options }),
});

export const { t, tPlural } = translate;

export const defaultTranslateFields = ['label', 'placeholder', 'subLabel', 'supportingText', 'tooltip'];

/**
 * Helper method that iterates through all nested objects/arrays and runs `translateField` conversions. Since
 * the primary objects that need to be translated are StepField or TableSettings, the defaultTranslateFields is used
 * as a default argument for the fields parameter.
 */
export const translateFields = (
  translatableFields: Record<string, any>,
  fields: null | string[] = defaultTranslateFields,
  ignoreFields: string[] = ['id'],
  deepCopy = true
): any => {
  const translationObject = deepCopy ? cloneDeep(translatableFields) : translatableFields;
  for (const field in translationObject) {
    if (typeof translationObject[field] === 'object') {
      translateFields(translationObject[field], fields, ignoreFields, false);
    }
  }

  return !!translationObject && translateField(translationObject, fields, ignoreFields);
};

/**
 * Helper method that converts an object's properties specified by fields into a dynamic getter that
 * translates that properties string value
 *
 * - translateField(obj, ['a','b','c']) <-  only a, b, c will be translated getters
 * - translateField(obj, defaultTranslatedFields.concat(['a','b','c'])) <-  a, b, c will be translated getters in
 * addition to label, placeholder, sublabel
 * - translateField(obj) <-  label, placeholder, sublabel will be translated getters
 * - translateField(obj, null) <-  all fields in obj that is a string value will be a translated getter
 * - translateField(obj, null, ['ignore_me']) <-  all fields except for 'ignore_me' in obj that is a string value
 * will be a translated getter
 */
export const translateField = (
  translateObjectFields: Record<string, any>,
  fields: null | string[] = defaultTranslateFields,
  ignoreFields: string[] = ['id']
): any => {
  for (const prop of Object.keys(translateObjectFields)) {
    if ((fields && !fields.includes(prop)) || ignoreFields.includes(prop)) {
      continue;
    }
    const originalProp: Intl = get(translateObjectFields, prop);
    if (isString(originalProp)) {
      Object.defineProperty(translateObjectFields, prop, { get: () => translate.t(originalProp) });
    }
  }

  return translateObjectFields;
};

/**
 * Sets the locale and triggers a language change. If the requested locale's resources
 * haven't been loaded yet (e.g. first login where UserStoreInitializer sets a different locale),
 * reloads the page so bootSequence can load the correct locale from storage.
 */
export const setLocale = (locale: Locale) => {
  localeStorage.set(locale);
  if (i18next.default.language !== locale) {
    if (!i18next.default.hasResourceBundle(locale, 'translation')) {
      globalThis.location.reload();

      return;
    }
    void i18next.changeLanguage(locale);
  }
  if (Settings.defaultLocale !== locale) {
    Settings.defaultLocale = locale;
  }
};

/**
 * Format a month title based on locale. Given a date, will return full month name + year.
 */
export const customFormatMonthTitle = (date: Date, locale: Locale) => {
  const month = date.toLocaleString(locale, { month: 'long' });

  return `${month} ${date.getFullYear()}`;
};
